import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import cx from "classnames";

import { Icon } from "../ui/icon";
import PageList from "./PageList";
import LaserCursor from "./LaserCursor";
import useFullScreen from "../../hooks/useFullScreen";
import { isOnMobileDevice, stopAllResourceBuffering } from "../../_helpers/utils";

import global from "../../scss/dhp.scss";
import useWidgetAnimation from "../../hooks/useWidgetAnimation";
import useAudioPlayer from "../../hooks/useAudioPlayer";
import { Input } from "../ui/input";
import { TYPE_INTERACTIVE_DEMO } from "../../constants/editor";
import useButtonAnimations from "../../hooks/useButtonAnimations";

let style = Object.assign({}, global);

const PageControls = ({
  playVideos,
  stopVideos,
  slideIndex,
  setSlideIndex,
  setAdjustment,
  published,
  ready,
  ...props
}) => {
  const [mobileDevice, setMobileDevice] = useState(false);
  const [showLaserCursor, setShowLaserCursor] = useState(false);
  const [isVideoPresent, setIsVideoPresent] = useState(false);
  const [muted, setMuted] = useState(true);
  const [pagelistIndex, setPagelistIndex] = useState(1);
  const [meta, setMeta] = useState({ firstLoad: true, index: 1 });
  const { documentDetails } = useSelector(state => state.document);
  const docContexts = published
    ? documentDetails?.document?.rawdata?.contexts
    : documentDetails?.data?.version?.rawdata?.contexts;
  const totalPages = docContexts?.pageNodes?.length;
  const [fullScreen, toggleFullScreen] = useFullScreen(
    props?.isEmbed ?? false,
    showLaserCursor,
    slideIndex,
    totalPages
  );
  const { handleWidgetEntranceAnimation, handleWidgetExitAnimation, isAnimating } = useWidgetAnimation({ docContexts });
  useAudioPlayer({ docPagesMeta: props.docPagesMeta, origin: "present", activePageIdx: pagelistIndex, muted }); //audio player
  const { createRipple, createPulse, createPreviewSpinner } = useButtonAnimations();

  const muteRef = useRef(true);
  const activeButtonAnimationsRef = useRef([]);
  const currentAudioRef = useRef(null);
  const prevSlideIndexRef = useRef(null);
  const totalSlidesRef = useRef(0);
  const isAudioPlayingRef = useRef(false);

  const [replayTrigger, setReplayTrigger] = useState(0);

  const toggleMute = (mute, triggerSource) => {
    setMuted(mute);
    document.querySelectorAll(`#dhp-page-${slideIndex - 1} video,  audio`).forEach(v => (v.muted = mute));
    if (triggerSource === "userMuteAction") muteRef.current = mute;
  };

  const onExit = () => {
    stopAudio();
    stopButtonAnimations();
    stopAllResourceBuffering(document, "video");
    if (published && !fullScreen) {
      setShowLaserCursor(false);
      setTimeout(() => {
        props?.setShowPublishedPresent(false);
      }, 100);
      return;
    }

    if (fullScreen) toggleFullScreen(true);
    else {
      setShowLaserCursor(false);
      window.history.back();
    }
  };

  const gotoSlide = n => {
    let newSlideIndex = slideIndex + n > totalPages ? 1 : slideIndex + n === 0 ? totalPages : slideIndex + n;
    setPagelistIndex(newSlideIndex);
  };

  const showSlides = slideNo => {
    let slides =
      props.source !== "preview"
        ? document.getElementsByClassName("present-page")
        : document.getElementsByClassName("preview-page");

    [...slides].forEach((element, i) => {
      element.classList.add("d-none");
      if (slideNo - 1 !== i) stopVideos(i);
    });
    slides[slideNo - 1]?.classList.remove("d-none");

    // if (props.source !== "preview") {
    //   handleWidgetEntranceAnimation(slideNo - 1); // passing the active page IDX
    // }

    setTimeout(() => {
      playVideos(slideNo - 1, muteRef.current);
    }, 100);
  };

  const checkDevice = () => {
    setMobileDevice(isOnMobileDevice());
  };

  const handleKeyDown = event => {
    if (event.key === "ArrowLeft" && pagelistIndex !== 1) gotoSlide(-1);
    if (event.key === "ArrowRight" && pagelistIndex !== totalPages) gotoSlide(1);
  };

  const updateCustomZoomValueOnchange = e => {
    if (e.target?.validity.patternMismatch) return;
    props?.setContiniousDocumentZoom(e.target.value);
  };

  const updateCustomZoomValue = e => {
    if (e && ((e.type === "keyup" && 13 === e.keyCode) || e.type === "blur"))
      if (isNaN(Math.round(e.target.value)) || e.target.value == "") {
        props?.setContiniousDocumentZoom(props?.continiousDocumentZoom);
      } else {
        props?.setContiniousDocumentZoom(props.validateZoomRange(Math.round(e.target.value)));
      }
  };

  const handleButtonAnimation = page => {
    const buttons = page.querySelectorAll("[data-button-animation]");

    buttons.forEach(button => {
      const animationType = button.getAttribute("data-button-animation");

      if (!animationType) return;

      let animationInstance;

      if (animationType === "Ripple") {
        const el = button.querySelector(`.dhp-widget-inner`);

        el.style.overflow = "hidden";
        el.style.position = "relative"; // VERY IMPORTANT

        // loop ripple
        const interval = setInterval(() => {
          createRipple(el);
        }, 1200);

        animationInstance = { type: "interval", value: interval };
      }

      if (animationType === "Pulse") {
        const pulse = createPulse(button);
        animationInstance = { type: "element", value: pulse };
      }

      if (animationType === "Spinner") {
        const spinner = createPreviewSpinner(button);
        animationInstance = { type: "element", value: spinner };
      }

      if (animationInstance) {
        activeButtonAnimationsRef.current.push({
          button,
          animationInstance,
        });
      }
    });
  };

  const stopButtonAnimations = () => {
    activeButtonAnimationsRef.current.forEach(({ animationInstance }) => {
      if (animationInstance.type === "interval") {
        clearInterval(animationInstance.value);
      }

      if (animationInstance.type === "element") {
        animationInstance.value?.remove();
      }
    });

    activeButtonAnimationsRef.current = [];
  };

  const handleCalloutAnimation = ({ widgetId, widgetNode }) => {
    let hasAnimation = false;
    let hasAudio = false;

    let delayBeforeAnimation = 800; // delay before zoom starts
    const animationDuration = 900; // your transform duration
    const audioStartOffset = 300; // start audio DURING animation

    try {
      const config = JSON.parse(widgetNode.getAttribute("data-config"));
      hasAnimation = config?.animation === true;
      hasAudio = config?.enableVoiceOver === true;

      delayBeforeAnimation = config?.animationDelayMs ?? 800;
    } catch (e) {}

    const activePage = widgetNode.closest(".present-page");
    if (!activePage) return;

    if (hasAnimation) {
      // 1️⃣ Start animation
      setTimeout(() => {
        focusCallout(activePage, widgetNode);
      }, delayBeforeAnimation);

      // 2️⃣ Start audio DURING animation
      if (hasAudio) {
        setTimeout(() => {
          playCalloutAudio(widgetNode, muted);
        }, delayBeforeAnimation + audioStartOffset);
      }
    } else {
      resetPreview(activePage);

      // no animation → play immediately
      if (hasAudio) {
        playCalloutAudio(widgetNode, muted);
      }
    }
  };

  const playCalloutAudio = (widgetNode, muted) => {
    let audioSrc = "";
    let calloutVoiceEnable;

    try {
      const config = JSON.parse(widgetNode.getAttribute("data-config"));
      audioSrc = config?.voiceOverAudioFile;
      calloutVoiceEnable = config?.enableVoiceOver;
    } catch (e) {}

    if (!audioSrc || !calloutVoiceEnable) return;

    // prevent duplicate play
    if (isAudioPlayingRef.current) return;

    stopAudio();

    if (muted) return;

    const audio = new Audio(audioSrc);

    currentAudioRef.current = audio;
    isAudioPlayingRef.current = true;

    audio.play().catch(() => {});

    audio.onended = () => {
      if (currentAudioRef.current === audio) {
        currentAudioRef.current = null;
        isAudioPlayingRef.current = false;
      }
    };
  };

  const getCurrentPageCallout = () => {
    const slides = document.querySelectorAll(".present-page");
    const currentIndex = slideIndex - 1;

    const currentPage = slides[currentIndex];
    if (!currentPage) return null;

    return currentPage.querySelector('[data-widget="CALLOUT"]');
  };

  const stopAudio = () => {
    const audio = currentAudioRef.current;

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      currentAudioRef.current = null;
    }

    isAudioPlayingRef.current = false; // reset lock
  };

  const focusCallout = (page, callout) => {
    const previewLayer = page.querySelector('div[style*="transform-origin"]');
    if (!previewLayer) return;

    // RESET
    previewLayer.style.transition = "none";
    previewLayer.style.transform = "scale(1)";
    previewLayer.style.transformOrigin = "center center";

    previewLayer.getBoundingClientRect(); // force reflow

    const pageRect = page.getBoundingClientRect();
    const rect = callout.getBoundingClientRect();

    // get callout center RELATIVE to page
    const originX = ((rect.left - pageRect.left + rect.width / 2) / pageRect.width) * 100;
    const originY = ((rect.top - pageRect.top + rect.height / 2) / pageRect.height) * 100;

    // move "camera focus" instead of moving page
    previewLayer.style.transformOrigin = `${originX}% ${originY}%`;

    // very small zoom (Supademo style)
    const scale = 1.08;

    previewLayer.style.transition = "transform 900ms cubic-bezier(0.22, 1, 0.36, 1)";

    previewLayer.style.transform = `scale(${scale})`;
  };

  const resetPreview = page => {
    const previewLayer = page.querySelector('div[style*="transform-origin"]');

    if (!previewLayer) return;

    previewLayer.style.transition = "none";
    previewLayer.style.transform = "translate(0px, 0px) scale(1)";
  };

  // useEffect(() => {
  //   // showSlides(slideIndex);
  //   setPagelistIndex(slideIndex);
  //   setIsVideoPresent(document.querySelectorAll(`#dhp-page-${slideIndex - 1} video, audio`).length > 0);
  //   toggleMute(true);
  //   if (!muteRef.current)
  //     setTimeout(() => {
  //       toggleMute(false);
  //     }, 10);
  // }, [slideIndex]);

  useEffect(() => {
    if (!ready) return;
    if (meta?.firstLoad) {
      setSlideIndex(meta.index);
      showSlides(meta.index);
      setMeta({ ...meta, firstLoad: false });
    } else {
      let delay = handleWidgetExitAnimation(slideIndex - 1);
      setTimeout(() => {
        setSlideIndex(meta.index);
        showSlides(meta.index);
      }, delay - 10);
    }
  }, [meta?.index, ready]);

  useEffect(() => {
    setMeta({ ...meta, index: pagelistIndex, firstLoad: false });
  }, [pagelistIndex]);

  useEffect(() => {
    if (fullScreen) setAdjustment(0);
    else setAdjustment(60);
  }, [fullScreen]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [slideIndex]);

  useEffect(() => {
    checkDevice();
    window.addEventListener("resize", checkDevice);

    return () => {
      window.removeEventListener("resize", checkDevice);
    };
  }, []);

  useEffect(() => {
    if (props.setIsAudioMuted) props.setIsAudioMuted(muted);

    if (muted) {
      // stop immediately
      stopAudio();
    } else {
      // play ONLY current page callout
      const callout = getCurrentPageCallout();

      if (callout) {
        playCalloutAudio(callout, false);
      }
    }
  }, [muted]);

  useEffect(() => {
    const slides = document.querySelectorAll(".present-page");

    if (!slides) return;

    const prevIndex = prevSlideIndexRef.current;
    const currentIndex = slideIndex - 1;

    const prevPage = slides[prevIndex];
    const currentPage = slides[currentIndex];

    // STOP PREVIOUS SLIDE STUFF
    stopAudio();
    stopButtonAnimations();

    if (prevPage) {
      resetPreview(prevPage);
    }

    // UPDATE STATE
    setPagelistIndex(slideIndex);
    setIsVideoPresent(document.querySelectorAll(`#dhp-page-${slideIndex - 1} video, audio`).length > 0);

    // HANDLE MUTE SYNC
    toggleMute(true);

    if (!muteRef.current) {
      setTimeout(() => {
        toggleMute(false);
      }, 10);
    }

    // SWITCH SLIDES (IMPORTANT)
    showSlides(slideIndex);

    // wait for DOM/layout stabilization
    // setTimeout(() => {
    if (props.source === "preview") return;

    handleWidgetEntranceAnimation(currentIndex);

    // START CURRENT SLIDE LOGIC
    if (currentPage) {
      const callout = currentPage.querySelector('[data-widget="CALLOUT"]');

      // CALLOUT
      if (callout) {
        const widgetId = callout.getAttribute("data-id");

        setTimeout(() => {
          handleCalloutAnimation({
            widgetId,
            widgetNode: callout,
          });
        }, 300);
      }

      // BUTTON
      if (currentPage.querySelector('[data-widget="BUTTON"]')) {
        setTimeout(() => {
          handleButtonAnimation(currentPage);
        }, 80);
      }

      // REPLAY ICON SHOW/HIDE
      const replayIcon = document.querySelector(".replay-icon");

      if (replayIcon) {
        if (slideIndex === totalSlidesRef.current) {
          replayIcon.classList.remove("d-none");
        } else {
          replayIcon.classList.add("d-none");
        }
      }
    }
    // }, 80);

    // SAVE PREVIOUS INDEX
    prevSlideIndexRef.current = currentIndex;
  }, [slideIndex, replayTrigger]);

  useEffect(() => {
    if (props.source === "preview") return;

    const handleClick = e => {
      const leftArrow = e.target.closest(".icon-ui-arrow-left2");
      const rightArrow = e.target.closest(".icon-ui-arrow-right2");

      if (leftArrow) {
        gotoSlide(-1);
      }

      if (rightArrow) {
        gotoSlide(1);
      }
    };

    const handleReplayClick = e => {
      const replayIcon = e.target.closest(".replay-icon");

      if (!replayIcon) return;

      e.stopPropagation();

      // STOP OLD STUFF
      stopAudio();
      stopButtonAnimations();

      // RESET ALL PAGES
      const slides = document.querySelectorAll(".present-page");

      slides.forEach(page => {
        resetPreview(page);
        page.classList.add("d-none");
      });

      // RESET TRACKER
      prevSlideIndexRef.current = null;

      // GO TO FIRST SLIDE
      if (slides.length === 1) {
        setReplayTrigger(prev => prev + 1);
      } else {
        setSlideIndex(1);
      }
      setPagelistIndex(1);
    };

    document.addEventListener("click", handleClick);
    document.addEventListener("click", handleReplayClick);

    const arrows = document.querySelectorAll(".icon-ui-arrow-left2, .icon-ui-arrow-right2");

    arrows.forEach(el => {
      el.style.cursor = "pointer";
    });

    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("click", handleReplayClick);
    };
  }, [slideIndex]);

  useEffect(() => {
    totalSlidesRef.current = totalPages;
  }, [totalPages]);

  return (
    <>
      <LaserCursor isOpen={showLaserCursor} />
      <div className={cx(style["slide-controls"], style["editor-navigation"], style["rounded"])}>
        <div className={cx(style["page-info-wdg"])}>
          <ul>
            {props?.continiousDocumentZoom && (
              <>
                <li>
                  <div
                    onClick={() => {
                      props?.setContiniousDocumentZoom(
                        parseInt(props.validateZoomRange(Math.round(props?.continiousDocumentZoom) - 10))
                      );
                    }}
                    className={cx(style["custom-tooltip"], style["link-white"])}>
                    <Icon icon="ui-minus-circle" />
                    <div className={cx(style["custom-tooltip-content"], style["top"])}>Zoom Out</div>
                  </div>
                </li>

                <li className={style["zoom-percent"]}>
                  <span>
                    <Input
                      name="zoom-value"
                      type="text"
                      pattern="[0-9]*"
                      returnType="noGroup"
                      value={Math.round(props?.continiousDocumentZoom)}
                      onChange={e => updateCustomZoomValueOnchange(e)}
                      onBlur={e => updateCustomZoomValue(e)}
                      onKeyUp={e => updateCustomZoomValue(e)}
                    />
                    %
                  </span>
                </li>

                <li>
                  <div
                    onClick={() => {
                      props?.setContiniousDocumentZoom(
                        parseInt(props.validateZoomRange(Math.round(props?.continiousDocumentZoom) + 10))
                      );
                    }}
                    className={cx(style["custom-tooltip"], style["link-white"])}>
                    <Icon icon="ui-plus-circle" />
                    <div className={cx(style["custom-tooltip-content"], style["top"])}>Zoom In</div>
                  </div>
                </li>
              </>
            )}
            {totalPages > 1 && (
              <>
                <li className={cx(style["d-flex"], style["align-items-center"])}>
                  <div
                    className={cx(style["custom-tooltip"], style["link-white"], {
                      [style["disable"]]: slideIndex === 1 || isAnimating,
                    })}>
                    <Icon icon="ui-arrow-left3" onClick={() => gotoSlide(-1)} id="prev" />
                    <div className={cx(style["custom-tooltip-content"], style["top"])}>Previous</div>
                  </div>
                  <span>
                    {slideIndex}/{totalPages}
                  </span>
                  <div
                    className={cx(style["custom-tooltip"], style["link-white"], {
                      [style["disable"]]: slideIndex === totalPages || isAnimating,
                    })}>
                    <Icon icon="ui-arrow-right3" onClick={() => gotoSlide(1)} id="next" />
                    <div className={cx(style["custom-tooltip-content"], style["top"])}>Next</div>
                  </div>
                </li>
                <li className={style["separator"]}></li>
                {!props?.isEmbed && (
                  <PageList
                    setSlideIndex={setPagelistIndex}
                    slideIndex={pagelistIndex}
                    fullScreen={fullScreen}
                    published={published}
                    component="Present"
                    docPagesMeta={props?.docPagesMeta}
                  />
                )}
              </>
            )}
            {(isVideoPresent || props.isAudioPresent) && (
              <li className={style["custom-tooltip"]}>
                <div
                  className={cx(style["link-white"])}
                  onClick={() => toggleMute(!muted, "userMuteAction")}
                  id="toggleMute">
                  <Icon icon={`ui-${muted ? "volume-mute" : "volume"}`} />
                </div>
                <div className={cx(style["custom-tooltip-content"], style["top"])}>{muted ? "Unmute" : "Mute"}</div>
              </li>
            )}
            {!mobileDevice && (
              <li className={style["custom-tooltip"]}>
                <div className={cx(style["link-white"])}>
                  <Icon icon="ui-cursor" onClick={() => setShowLaserCursor(!showLaserCursor)} />
                </div>
                <div className={cx(style["custom-tooltip-content"], style["top"])}>Pointer</div>
              </li>
            )}
            {!props?.isEmbed && (
              <>
                <li className={style["custom-tooltip"]}>
                  <div className={cx(style["link-white"])} onClick={() => toggleFullScreen(fullScreen)}>
                    <Icon icon={`${fullScreen ? "contract" : "ui-expand"}`} />
                  </div>
                  <div className={cx(style["custom-tooltip-content"], style["top"])}>
                    {fullScreen ? "Exit Fullscreen" : "Fullscreen"}
                  </div>
                </li>
                {props.source !== "preview" && (
                  <>
                    <li className={style["separator"]}></li>
                    <li className={style["custom-tooltip"]}>
                      <div className={cx(style["link-white"])} onClick={onExit}>
                        <Icon icon="ui-close" />
                      </div>
                      <div className={cx(style["custom-tooltip-content"], style["top"])}>Exit</div>
                    </li>
                  </>
                )}
                {props.docPagesMeta?.documentType === TYPE_INTERACTIVE_DEMO && (
                  <li className={style["custom-tooltip"]}>
                    <div className={cx(style["link-white"], style["d-none"], "replay-icon")}>
                      <Icon icon="ui-refresh" />
                    </div>
                    <div className={cx(style["custom-tooltip-content"], style["top"])}>Replay</div>
                  </li>
                )}
              </>
            )}
          </ul>
        </div>
      </div>
    </>
  );
};

export default PageControls;

PageControls.propTypes = {
  setSlideIndex: PropTypes.func.isRequired,
  slideIndex: PropTypes.number.isRequired,
  playVideos: PropTypes.func.isRequired,
  stopVideos: PropTypes.func.isRequired,
  setAdjustment: PropTypes.func.isRequired,
  published: PropTypes.bool,
  setShowPublishedPresent: PropTypes.func,
  isEmbed: PropTypes.bool,
  docPagesMeta: PropTypes.object,
};
